/**
 * Billing & Payables PDF — same LCTF CRM report chrome as `exportToPDF`
 * (forest-green header, beige summary, autotable), with AR and AP kept
 * as separate totals so they are not summed into one amount.
 */

import type { ProjectBillingSummary, ProjectMoneyLine } from './projectBillingSummary';
import { LCTF_OFFER_ADDRESS, LCTF_OFFER_COMPANY } from './jobOfferLetter';
import {
  expandSupplierBillPayments,
  installmentApprovalRequired,
  type SupplierBill,
  type SupplierBillPaymentSlice,
} from './supplierBills';

export type BillingPayablesReportType = 'Invoice' | 'Payment' | 'Quote';

export interface BillingPayablesReportRow {
  project: string;
  type: BillingPayablesReportType;
  item: string;
  detail: string;
  amount: number;
  due: string;
  status: string;
}

export interface BillingPayablesReportTotals {
  ar: number;
  ap: number;
}

export type BillingPayablesStatusFilter =
  | 'all'
  | 'to_invoice'
  | 'invoiced'
  | 'awaiting_approval'
  | 'to_pay'
  | 'paid'
  | 'quotes';

/** Which side of the ledger a PDF includes. `all` keeps invoices, bills, and quotes. */
export type BillingPayablesLedger = 'all' | 'ar' | 'ap';

export interface ExportBillingPayablesPdfInput {
  summaries: ProjectBillingSummary[];
  totals: BillingPayablesReportTotals;
  filterLabel: string;
  statusFilter?: BillingPayablesStatusFilter;
  /** Defaults to the combined report. */
  ledger?: BillingPayablesLedger;
  taskEndDateById?: Map<string, Date>;
  now?: Date;
}

export function billingPayablesReportTitle(ledger: BillingPayablesLedger = 'all'): string {
  if (ledger === 'ar') return 'Accounts Receivable';
  if (ledger === 'ap') return 'Accounts Payable';
  return 'Billing & Payables';
}

/**
 * Receivables are customer invoices (`in`). Payables are vendor bills (`out`)
 * and open quotes.
 */
export function lineIncludedInBillingLedger(
  direction: ProjectMoneyLine['direction'],
  ledger: BillingPayablesLedger = 'all',
): boolean {
  if (ledger === 'ar') return direction === 'in';
  if (ledger === 'ap') return direction !== 'in';
  return true;
}

export function summariesForBillingLedger(
  summaries: ProjectBillingSummary[],
  ledger: BillingPayablesLedger = 'all',
): ProjectBillingSummary[] {
  if (ledger === 'all') return summaries;
  return summaries
    .map((summary) => ({
      ...summary,
      lines: summary.lines.filter((line) => lineIncludedInBillingLedger(line.direction, ledger)),
    }))
    .filter((summary) => summary.lines.length > 0);
}

export function isPaymentStateFilter(
  filter: BillingPayablesStatusFilter,
): boolean {
  return filter === 'awaiting_approval' || filter === 'to_pay' || filter === 'paid';
}

export function installmentSliceMatchesFilter(
  bill: SupplierBill,
  slice: SupplierBillPaymentSlice,
  filter: BillingPayablesStatusFilter,
): boolean {
  if (!isPaymentStateFilter(filter)) return true;
  const idx = slice.installmentIndex;
  if (idx === undefined) return true;
  const paid = !!bill.installmentPayments?.[idx] || bill.status === 'paid';
  const approved = !!bill.installmentApprovals?.[idx];
  const approvalReq = installmentApprovalRequired(bill.paymentTerms);
  if (filter === 'paid') return paid;
  if (filter === 'awaiting_approval') return !paid && approvalReq && !approved;
  if (filter === 'to_pay') return !paid && (!approvalReq || approved);
  return true;
}

function installmentRowStatus(
  bill: SupplierBill,
  slice: SupplierBillPaymentSlice,
): string {
  const idx = slice.installmentIndex;
  if (idx === undefined) return formatBillingPayablesStatus(bill.status);
  if (bill.installmentPayments?.[idx] || bill.status === 'paid') return 'Paid';
  if (installmentApprovalRequired(bill.paymentTerms) && !bill.installmentApprovals?.[idx]) {
    return 'Awaiting';
  }
  return 'To pay';
}

const TYPE_BY_DIRECTION: Record<ProjectMoneyLine['direction'], BillingPayablesReportType> = {
  in: 'Invoice',
  out: 'Payment',
  quote: 'Quote',
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);

/**
 * Helvetica (jsPDF built-in) only measures WinAnsi/ASCII reliably. Any other
 * glyph gets width 0, and autotable then stretches the whole cell — the
 * "D u e   p r i o r   t o   s h i p m e n t" tracking bug.
 */
export function toPdfSafeText(raw: string): string {
  return (raw || '')
    .replace(/[\u2014\u2013\u2212\u00B7\u2022]/g, '-')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u00A0\u202F\u2007\u2008\u2009\u200A\u200B\uFEFF]/g, ' ')
    .replace(/\u00AD/g, '')
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function formatBillingPayablesDue(dueDate?: Date | null, estimated?: boolean): string {
  if (!dueDate) return '-';
  const day = dueDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return toPdfSafeText(estimated ? `${day}*` : day);
}

/** Collapse noisy schedule / vendor copy so table cells wrap on real words. */
function moneyLineReportDetail(line: ProjectMoneyLine): string {
  return cleanBillingPayablesDetail(
    [line.detail, line.poNumber].filter((part) => !!part?.trim()).join(' · '),
  );
}

export function cleanBillingPayablesDetail(raw: string): string {
  let s = (raw || '').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  // Split only on decorative dashes / bullets — keep hyphens in CO refs.
  s = s.replace(/\s*[—–·•]\s*/g, '\u0001');
  s = s.replace(/\(\s*["“”'']?\s*/g, '(').replace(/\s+\)/g, ')');
  s = s.replace(/\(\s*\)/g, '');
  s = s.replace(/\s*\u0001\s*includes\s+(\d+)\s+bundled change orders?/i, '\u0001$1 bundled COs');
  const parts = s.split('\u0001').map((p) => p.trim()).filter(Boolean);
  const kept: string[] = [];
  for (const part of parts) {
    const overlap = kept.findIndex((k) => isSameOrExtendedLabel(k, part));
    if (overlap < 0) {
      kept.push(part);
      continue;
    }
    if (part.length > kept[overlap].length) kept[overlap] = part;
  }
  return toPdfSafeText(kept.join(' - '));
}

/** True when labels are equal or one is the other plus a parenthetical / suffix. */
function isSameOrExtendedLabel(a: string, b: string): boolean {
  const al = a.toLowerCase();
  const bl = b.toLowerCase();
  if (al === bl) return true;
  const extendsWith = (base: string, full: string) =>
    full.startsWith(`${base} `) || full.startsWith(`${base}(`) || full.startsWith(`${base} (`);
  return extendsWith(al, bl) || extendsWith(bl, al);
}

export function formatBillingPayablesStatus(label: string): string {
  const s = toPdfSafeText(label);
  if (!s) return '-';
  if (/^invoiced in qb\b/i.test(s)) return s.replace(/^Invoiced in QB\s*/i, 'Invoiced ');
  if (/^approved\b/i.test(s) && /no bill/i.test(s)) return 'Approved';
  if (/^quoted\b/i.test(s) && /not awarded/i.test(s)) return 'Quoted';
  if (/^ready to (be )?paid$/i.test(s)) return 'To pay';
  if (/^pending review$/i.test(s)) return 'Review';
  if (/^awaiting approval$/i.test(s)) return 'Awaiting';
  if (/^entered in qb$/i.test(s)) return 'In QB';
  return s;
}

export function billingPayablesReportFilename(
  now: Date = new Date(),
  ledger: BillingPayablesLedger = 'all',
): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const stem = ledger === 'ar'
    ? 'Accounts-Receivable'
    : ledger === 'ap'
      ? 'Accounts-Payable'
      : 'Billing-Payables';
  return `${stem}-${y}-${m}-${d}.pdf`;
}

export function buildBillingPayablesReportRows(
  summaries: ProjectBillingSummary[],
  options?: {
    statusFilter?: BillingPayablesStatusFilter;
    ledger?: BillingPayablesLedger;
    taskEndDateById?: Map<string, Date>;
  },
): BillingPayablesReportRow[] {
  const filter = options?.statusFilter ?? 'all';
  const ledger = options?.ledger ?? 'all';
  const rows: BillingPayablesReportRow[] = [];
  for (const summary of summaries) {
    for (const line of summary.lines) {
      if (!lineIncludedInBillingLedger(line.direction, ledger)) continue;
      const bill = line.supplierBill;
      if (
        bill
        && isPaymentStateFilter(filter)
        && bill.paymentTerms?.installments?.length
      ) {
        const slices = expandSupplierBillPayments(bill, {
          taskEndDateById: options?.taskEndDateById,
        }).filter(
          (s) =>
            s.installmentIndex !== undefined
            && installmentSliceMatchesFilter(bill, s, filter),
        );
        for (const slice of slices) {
          rows.push({
            project: toPdfSafeText(summary.dealLabel),
            type: 'Payment',
            item: toPdfSafeText(slice.label || line.label),
            detail: moneyLineReportDetail(line),
            amount: Math.round(slice.amount),
            due: formatBillingPayablesDue(slice.dueDate, slice.estimated),
            status: installmentRowStatus(bill, slice),
          });
        }
        continue;
      }
      rows.push({
        project: toPdfSafeText(summary.dealLabel),
        type: TYPE_BY_DIRECTION[line.direction],
        item: toPdfSafeText(line.label),
        detail: moneyLineReportDetail(line),
        amount: Math.round(Number(line.amount) || 0),
        due: formatBillingPayablesDue(line.dueDate, line.estimated),
        status: formatBillingPayablesStatus(line.statusLabel),
      });
    }
  }
  return rows;
}

const LETTERHEAD_LOGO_MAX_PX = 720;

/**
 * jsPDF embeds the decoded pixels. The lockup is ~3200px wide, which turns a
 * one-page report into a huge file, so draw it from a smaller canvas copy.
 */
function shrinkLetterheadLogo(dataUrl: string | undefined): Promise<string | undefined> {
  if (!dataUrl || typeof document === 'undefined') return Promise.resolve(dataUrl);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, LETTERHEAD_LOGO_MAX_PX / Math.max(img.width, img.height, 1));
      const width = Math.max(1, Math.round(img.width * scale));
      const height = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

const LETTERHEAD_GREEN: [number, number, number] = [44, 62, 47];
const LETTERHEAD_CREAM: [number, number, number] = [232, 228, 214];

/** Street, city, email, and phone — the same block used on other LCTF letters. */
export function billingPayablesLetterheadAddress(): string[] {
  return LCTF_OFFER_ADDRESS.split('\n').map((line) => toPdfSafeText(line)).filter(Boolean);
}

/**
 * Forest-green header: the white lockup (name is in the artwork) and the
 * address on the letterhead row, with the report title and generated date beneath.
 * Returns the y where body content should start.
 */
export function drawBillingPayablesLetterhead(
  doc: import('jspdf').jsPDF,
  args: {
    pageWidth: number;
    margin: number;
    title: string;
    generatedLabel: string;
    logoDataUrl?: string;
  },
): number {
  const headerH = 34;
  doc.setFillColor(...LETTERHEAD_GREEN);
  doc.rect(0, 0, args.pageWidth, headerH, 'F');

  let drewLogo = false;
  if (args.logoDataUrl) {
    try {
      const props = doc.getImageProperties(args.logoDataUrl);
      const ratio = props.width / Math.max(props.height, 1);
      const maxH = 18;
      const maxW = 72;
      let logoH = maxH;
      let logoW = logoH * ratio;
      if (logoW > maxW) {
        logoW = maxW;
        logoH = logoW / ratio;
      }
      const logoY = 3 + (maxH - logoH) / 2;
      doc.addImage(args.logoDataUrl, 'PNG', args.margin, logoY, logoW, logoH, undefined, 'FAST');
      drewLogo = true;
    } catch {
      drewLogo = false;
    }
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  if (!drewLogo) {
    doc.setFontSize(13);
    doc.text(toPdfSafeText(LCTF_OFFER_COMPANY), args.margin, 12);
  }

  doc.setFontSize(14);
  doc.text(args.title, args.margin, 28);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...LETTERHEAD_CREAM);
  const rightX = args.pageWidth - args.margin;
  let lineY = 7.2;
  for (const line of billingPayablesLetterheadAddress()) {
    doc.text(line, rightX, lineY, { align: 'right' });
    lineY += 3.3;
  }

  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text(args.generatedLabel, rightX, 28.5, { align: 'right' });

  return headerH + 8;
}

export async function exportBillingPayablesPdf(
  input: ExportBillingPayablesPdfInput,
): Promise<void> {
  const now = input.now ?? new Date();
  const ledger = input.ledger ?? 'all';
  const summaries = summariesForBillingLedger(input.summaries, ledger);
  const rows = buildBillingPayablesReportRows(summaries, {
    statusFilter: input.statusFilter,
    ledger,
    taskEndDateById: input.taskEndDateById,
  });
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'letter',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const { loadLctfLockupLogoBase64 } = await import('./lctfPdfLogo');
  const logoDataUrl = await shrinkLetterheadLogo(await loadLctfLockupLogoBase64());
  const dateStr = `Generated: ${now.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })}`;
  let yPosition = drawBillingPayablesLetterhead(doc, {
    pageWidth,
    margin,
    title: billingPayablesReportTitle(ledger),
    generatedLabel: dateStr,
    logoDataUrl,
  });

  doc.setTextColor(44, 62, 47);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Filters Applied:', margin, yPosition);
  yPosition += 5;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(79, 66, 58);
  if (ledger !== 'all') {
    doc.text(`• Report: ${billingPayablesReportTitle(ledger)}`, margin + 2, yPosition);
    yPosition += 5;
  }
  doc.text(`• Status: ${input.filterLabel}`, margin + 2, yPosition);
  yPosition += 9;

  doc.setFillColor(222, 219, 206);
  doc.roundedRect(margin, yPosition, pageWidth - margin * 2, 18, 3, 3, 'F');
  doc.setTextColor(44, 62, 47);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Summary', margin + 5, yPosition + 7);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const summaryBits = [
    `Projects: ${summaries.length}`,
    `Lines: ${rows.length}`,
  ];
  if (ledger !== 'ap') summaryBits.push(`AR: ${fmtMoney(input.totals.ar)}`);
  if (ledger !== 'ar') summaryBits.push(`AP: ${fmtMoney(input.totals.ap)}`);
  let xPos = margin + 5;
  for (const bit of summaryBits) {
    doc.text(bit, xPos, yPosition + 14);
    xPos += doc.getTextWidth(bit) + 14;
  }
  yPosition += 24;

  if (rows.length === 0) {
    doc.setTextColor(79, 66, 58);
    doc.setFontSize(10);
    doc.text('No matching lines.', margin, yPosition);
  } else {
    autoTable(doc, {
      startY: yPosition,
      head: [['Project', 'Type', 'Item', 'Detail', 'Amount', 'Due', 'Status']],
      body: rows.map((row) => [
        row.project,
        row.type,
        row.item,
        row.detail || '-',
        toPdfSafeText(fmtMoney(row.amount)),
        row.due,
        row.status,
      ]),
      margin: { left: margin, right: margin, bottom: 16 },
      tableWidth: pageWidth - margin * 2,
      showHead: 'everyPage',
      styles: {
        fontSize: 8,
        cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
        font: 'helvetica',
        overflow: 'linebreak',
        valign: 'middle',
        halign: 'left',
        lineColor: [222, 219, 206],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [44, 62, 47],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        valign: 'middle',
      },
      alternateRowStyles: {
        fillColor: [247, 247, 247],
      },
      columnStyles: {
        0: { cellWidth: 48 },
        1: { cellWidth: 18 },
        2: { cellWidth: 20 },
        3: { cellWidth: 91 },
        4: { cellWidth: 22, halign: 'right' },
        5: { cellWidth: 26, halign: 'right' },
        6: { cellWidth: 24.4 },
      },
      willDrawCell: () => {
        doc.setCharSpace(0);
      },
      didDrawPage: (data) => {
        doc.setCharSpace(0);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Page ${data.pageNumber}  |  * Estimated from the project schedule`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' },
        );
      },
    });
  }

  doc.save(billingPayablesReportFilename(now, ledger));
}
